import { FC, type UIEvent, useMemo, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Globe, Search } from "lucide-react"
import { Input } from "./ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Badge } from "./ui/badge"
import { useNavigate, useParams } from "react-router-dom"
import { useGetCustomerList } from "@/hooks/api/use-get-customer-list"
import { CustomerDTOStatusEnum } from "@/gen/api"
import { useAuth } from "@/hooks/useAuth"
import { AndroidBrandIcon, AppleBrandIcon } from "./PlatformBrandIcons"

const addMonths = (date: Date, months: number) => {
  const d = new Date(date)
  d.setMonth(d.getMonth() + months)
  return d
}

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "active":
      return "bg-admin-success"
    case "inactive":
      return "bg-muted"
    case "suspended":
      return "bg-admin-danger"
    case "deleted":
      return "bg-muted text-muted-foreground border-border hover:bg-muted"
    case "unregistered":
      return "bg-orange-500"
    default:
      return "bg-muted"
  }
}

const formatDateTime = (date?: string | null) => {
  if (!date) return "never used"
  return new Date(date).toLocaleString()
}

const formatDate = (date?: string | null) => {
  if (!date) return "N/A"
  return new Date(date).toLocaleDateString()
}

const isDeletedCustomer = (status?: string) => status?.toLowerCase() === "deleted"

const getDeletedCustomerPurgeDate = (
  deleteAccountTime?: string | null,
  purgeScheduledAt?: string | null,
) => {
  if (purgeScheduledAt) return new Date(purgeScheduledAt)
  if (deleteAccountTime) return addMonths(new Date(deleteAccountTime), 6)
  return null
}

const platformUsed = (
  customer: {
    lastLogins?: { web?: string; ios?: string; android?: string }
    platforms?: { web?: boolean; ios?: boolean; android?: boolean }
  },
  platform: "web" | "ios" | "android",
) => Boolean(customer.lastLogins?.[platform] || customer.platforms?.[platform])

const CustomerTable: FC = () => {
  const { councilId: councilIdUrl } = useParams()
  const { user } = useAuth()
  const councilId = useMemo(
    () => councilIdUrl ?? user?.councils?.[0]?.id?.toString(),
    [councilIdUrl, user?.councils],
  )
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [deviceTypeFilter, setDeviceTypeFilter] = useState("all")

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetCustomerList(
      councilId!,
      searchQuery || undefined,
      statusFilter !== "all" ? statusFilter : undefined,
    )

  const customers = data?.pages.flatMap((page) => page.customers) ?? []

  const filteredCustomers = customers.filter((customer) => {
    const matchesDeviceType =
      deviceTypeFilter === "all" ||
      (deviceTypeFilter === "ios" && customer.platforms?.ios) ||
      (deviceTypeFilter === "web" && customer.platforms?.web) ||
      (deviceTypeFilter === "android" && customer.platforms?.android)

    return matchesDeviceType
  })

  const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage) return

    const target = event.currentTarget
    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight
    if (distanceToBottom < 120) {
      fetchNextPage()
    }
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">
            Error loading customers: {error.message}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="flex min-h-0 flex-1 flex-col">
      <CardHeader className="shrink-0">
        <CardTitle>Customer Management</CardTitle>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 size-4 text-muted-foreground" />
            <Input
              placeholder="Search customer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value={CustomerDTOStatusEnum.Active}>Active</SelectItem>
                <SelectItem value={CustomerDTOStatusEnum.Unregistered}>Unregistered</SelectItem>
                <SelectItem value={CustomerDTOStatusEnum.Inactive}>Inactive</SelectItem>
                <SelectItem value={CustomerDTOStatusEnum.Suspended}>Suspended</SelectItem>
                <SelectItem value={CustomerDTOStatusEnum.Deleted}>Deleted</SelectItem>
              </SelectContent>
            </Select>

            <Select value={deviceTypeFilter} onValueChange={setDeviceTypeFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by Device type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="ios">iOS</SelectItem>
                <SelectItem value="web">Web</SelectItem>
                <SelectItem value="android">Android</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="min-h-0 flex-1 overflow-hidden">
        {isLoading && customers.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Loading customers...</p>
          </div>
        ) : (
          <div className="h-full space-y-4 overflow-y-auto pr-2" onScroll={handleListScroll}>
            {filteredCustomers.map((customer) => {
              const deletedCustomerPurgeDate = getDeletedCustomerPurgeDate(
                customer.deleteAccountTime,
                customer.purgeScheduledAt,
              )

              return (
                <div key={customer.id}>
                  {isDeletedCustomer(customer.status) ? (
                    <div
                      data-testid="deleted-customer-tile"
                      className="w-full border rounded-lg p-4 space-y-3 text-left bg-muted/40 text-muted-foreground opacity-75"
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <h3 className="font-medium">Deleted customer</h3>
                          <p className="text-sm text-muted-foreground">Email: {customer.email}</p>
                        </div>
                        <div className="text-right space-y-1">
                          <Badge className={getStatusColor(customer.status ?? "")}>
                            {customer.status}
                          </Badge>
                          <p className="text-xs text-muted-foreground">
                            Deleted: {formatDate(customer.deleteAccountTime)}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2 border-t">
                        <div className="space-y-1">
                          <p className="text-sm font-medium">Purge Date</p>
                          <p className="text-xs text-muted-foreground">
                            {deletedCustomerPurgeDate
                              ? deletedCustomerPurgeDate.toLocaleDateString()
                              : "N/A"}
                          </p>
                        </div>
                        <div className="flex items-end justify-end text-xs text-muted-foreground">
                          Data fully purged on{" "}
                          {deletedCustomerPurgeDate
                            ? deletedCustomerPurgeDate.toLocaleDateString()
                            : "N/A"}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="w-full border rounded-lg p-4 space-y-3 cursor-pointer text-left transition hover:border-primary/60 hover:bg-muted/40 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() =>
                        navigate(
                          `${councilId ? `/council/${councilId}` : ""}/customers/${customer.id}`,
                        )
                      }
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <h3 className="font-medium">Customer: {customer.name}</h3>
                          <p className="text-sm text-muted-foreground">Email: {customer.email}</p>
                          <p className="text-sm text-muted-foreground">
                            Address: {customer.address}
                          </p>
                        </div>
                        <div className="text-right space-y-1">
                          <Badge className={getStatusColor(customer.status ?? "")}>
                            {customer.status}
                          </Badge>
                          <p className="text-xs text-muted-foreground">
                            Last Login: {formatDateTime(customer.lastLogin ?? null)}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-2 border-t">
                        <div className="space-y-1">
                          <p className="text-sm font-medium">Platform Activity</p>
                          <div className="flex flex-wrap gap-3">
                            <Globe
                              className={`size-5 ${platformUsed(customer, "web") ? "text-blue-500" : "text-muted-foreground/40"}`}
                              aria-label="Web"
                            />
                            <AppleBrandIcon
                              className={`size-5 ${platformUsed(customer, "ios") ? "text-blue-500" : "text-muted-foreground/40"}`}
                              aria-label="iOS"
                            />
                            <AndroidBrandIcon
                              className={`size-5 ${platformUsed(customer, "android") ? "text-blue-500" : "text-muted-foreground/40"}`}
                              aria-label="Android"
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <p className="text-sm font-medium">Account Details</p>
                          <p className="text-xs text-muted-foreground">
                            Devices → {customer.devices} total number of devices
                          </p>
                        </div>

                        <div className="flex items-end justify-end text-xs text-muted-foreground">
                          Click tile to open details
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              )
            })}

            {isFetchingNextPage && (
              <div className="py-2 text-center text-sm text-muted-foreground">
                Loading more customers...
              </div>
            )}

            {filteredCustomers.length === 0 && !isLoading && (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No customers found matching your criteria.</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default CustomerTable
